// components/analytics/LiveStats.tsx
import React, { useEffect } from 'react';
import { Statistic, Typography, Button, Tooltip } from 'antd';
import { 
  UserOutlined, 
  DollarOutlined, 
  RiseOutlined, 
  FallOutlined,
  ReloadOutlined,
  WifiOutlined,
  DisconnectOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { useLiveStats, type LiveStatsFilters } from '../../../shared/hooks/useLiveStats';
import { useTheme } from '../../../contexts/ThemeContext';

const { Text } = Typography;

interface LiveStatsProps {
  filters?: LiveStatsFilters;
  onStatsChange?: (stats: any) => void;
  showHeader?: boolean;
  compact?: boolean;
}

export const LiveStats: React.FC<LiveStatsProps> = ({
  filters = {},
  onStatsChange
}) => {
  const { theme } = useTheme();
  
  const {
    stats,
    loading,
    error,
    lastUpdated,
    isConnected,
    clearError,
    connectionStatus
  } = useLiveStats(filters);

  // Notificar cambios al componente padre
  useEffect(() => {
    if (stats && onStatsChange) {
      onStatsChange(stats);
    }
  }, [stats, onStatsChange]);

  // Formatear número como moneda
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Formatear número con separadores
  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('es-AR').format(num);
  };

  // Obtener color del balance neto
  const getNetBalanceColor = (): string => {
    if (!stats) return '#8c8c8c';
    if (stats.netBalance > 0) return '#52c41a';
    if (stats.netBalance < 0) return '#f5222d';
    return '#faad14';
  };

  // Icono de conexión
  const getConnectionIcon = () => {
    switch (connectionStatus) {
      case 'connected':
        return <WifiOutlined style={{ color: '#52c41a' }} />;
      case 'connecting':
        return <ReloadOutlined spin style={{ color: '#faad14' }} />;
      case 'disconnected':
      case 'error':
        return <DisconnectOutlined style={{ color: '#f5222d' }} />;
      default:
        return <WifiOutlined style={{ color: '#8c8c8c' }} />;
    }
  };

  // Componente de tarjeta financiera profesional
  const FinancialCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    loading?: boolean;
    trend?: 'up' | 'down' | 'neutral';
    size: 'large' | 'medium';
  }> = ({ title, value, icon, color, loading, trend, size }) => {
    
    const isLarge = size === 'large';
    const cardHeight = isLarge ? 200 : 140;
    const iconSize = isLarge ? 48 : 32;
    const fontSize = isLarge ? '32px' : '20px';
    
    return (
      <div
        style={{
          height: cardHeight,
          background: theme === 'dark' 
            ? `linear-gradient(135deg, ${color}08 0%, ${color}03 50%, transparent 100%)`
            : `linear-gradient(135deg, ${color}05 0%, ${color}02 50%, transparent 100%)`,
          borderRadius: 16,
          border: theme === 'dark'
            ? `1px solid ${color}20`
            : `1px solid ${color}15`,
          position: 'relative',
          overflow: 'hidden',
          cursor: 'pointer',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          backdropFilter: 'blur(10px)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = `0 20px 40px ${color}15`;
          e.currentTarget.style.border = `1px solid ${color}40`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
          e.currentTarget.style.border = theme === 'dark' ? `1px solid ${color}20` : `1px solid ${color}15`;
        }}
      >
        {/* Patrón de fondo tipo mapa de calor */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '100%',
            height: '100%',
            background: `radial-gradient(circle at 85% 15%, ${color}12 0%, transparent 50%)`,
            zIndex: 1
          }}
        />
        
        {/* Líneas decorativas */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: 2,
            background: `linear-gradient(90deg, ${color} 0%, transparent 100%)`,
            zIndex: 2
          }}
        />
        
        {/* Contenido */}
        <div style={{ 
          padding: isLarge ? 24 : 20,
          position: 'relative',
          zIndex: 3,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <Text style={{ 
                fontSize: '11px',
                fontWeight: 500,
                color: color,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                margin: 0,
                opacity: 0.8
              }}>
                {title}
              </Text>
              
              {/* Indicador de estado */}
              <div style={{ 
                marginTop: 4,
                display: 'flex',
                alignItems: 'center',
                gap: 4
              }}>
                <div style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: isConnected ? '#52c41a' : '#f5222d',
                  boxShadow: `0 0 8px ${isConnected ? '#52c41a' : '#f5222d'}40`
                }} />
                <Text style={{ 
                  fontSize: '9px',
                  color: theme === 'dark' ? '#8c8c8c' : '#bfbfbf',
                  fontWeight: 500
                }}>
                  {isConnected ? 'LIVE' : 'OFFLINE'}
                </Text>
              </div>
            </div>
            
            <div style={{
              width: iconSize,
              height: iconSize,
              background: `linear-gradient(135deg, ${color}20, ${color}40)`,
              borderRadius: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color,
              fontSize: iconSize * 0.6,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${color}30`
            }}>
              {icon}
            </div>
          </div>
          
          {/* Valor principal */}
          <div style={{ marginTop: isLarge ? 16 : 8 }}>
            <Statistic
              value={value}
              loading={loading}
              valueStyle={{
                fontSize: fontSize,
                fontWeight: 700,
                color: theme === 'dark' ? '#fff' : '#262626',
                margin: 0,
                fontFamily: '"JetBrains Mono", "SF Mono", Monaco, Consolas, monospace'
              }}
            />
            
            {/* Trend indicator */}
            {trend && trend !== 'neutral' && (
              <div style={{
                marginTop: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 4
              }}>
                {trend === 'up' ? (
                  <RiseOutlined style={{ color: '#52c41a', fontSize: '10px' }} />
                ) : (
                  <FallOutlined style={{ color: '#f5222d', fontSize: '10px' }} />
                )}
                <Text style={{ 
                  fontSize: '10px',
                  color: trend === 'up' ? '#52c41a' : '#f5222d',
                  fontWeight: 600,
                  fontFamily: 'monospace'
                }}>
                  {trend === 'up' ? '+' : '-'} TENDENCIA
                </Text>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ 
      width: '100%',
      minHeight: '100vh',
      background: theme === 'dark'
        ? 'linear-gradient(135deg, #0f1419 0%, #1a1a1a 50%, #0d1117 100%)'
        : 'linear-gradient(135deg, #f8fafc 0%, #ffffff 50%, #f1f5f9 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Patrón de fondo */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: theme === 'dark'
            ? 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.05) 1px, transparent 0)'
            : 'radial-gradient(circle at 1px 1px, rgba(0,0,0,0.05) 1px, transparent 0)',
          backgroundSize: '20px 20px',
          zIndex: 1
        }}
      />
      
      {/* Header con indicadores de estado */}
      <div style={{
        position: 'relative',
        zIndex: 3,
        padding: '32px 32px 24px 32px',
        borderBottom: theme === 'dark' 
          ? '1px solid rgba(255,255,255,0.08)' 
          : '1px solid rgba(0,0,0,0.06)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: isConnected ? '#52c41a' : '#f5222d',
                boxShadow: `0 0 12px ${isConnected ? '#52c41a' : '#f5222d'}60`,
                animation: isConnected ? 'pulse 2s infinite' : 'none'
              }} />
              <Text style={{ 
                fontSize: '13px',
                fontWeight: 600,
                color: theme === 'dark' ? '#8c8c8c' : '#595959',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                LIVE TRADING STATS
              </Text>
            </div>
            
            {lastUpdated && (
              <Text style={{ 
                fontSize: '11px',
                color: theme === 'dark' ? '#434343' : '#bfbfbf',
                marginTop: 4,
                display: 'block',
                fontFamily: 'monospace'
              }}>
                Last update: {lastUpdated.toLocaleTimeString()}
              </Text>
            )}
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {getConnectionIcon()}
            {error && (
              <Tooltip title={`Error: ${error}`}>
                <Button
                  type="text"
                  icon={<WarningOutlined />}
                  onClick={clearError}
                  size="small"
                  style={{ color: '#f5222d' }}
                />
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      {/* Grid de estadísticas financieras */}
      <div style={{
        position: 'relative',
        zIndex: 2,
        padding: '32px',
        display: 'grid',
        gridTemplateColumns: '2fr 1fr 1fr 1.5fr',
        gridTemplateRows: '1fr',
        gap: 24,
        height: 'calc(100vh - 140px)',
        minHeight: 400
      }}>
        {/* Balance Neto - Card principal más grande */}
        <FinancialCard
          title="Balance Neto"
          value={stats ? formatCurrency(stats.netBalance) : '$0'}
          icon={<DollarOutlined />}
          color={getNetBalanceColor()}
          loading={loading}
          trend={stats && stats.netBalance > 0 ? 'up' : stats && stats.netBalance < 0 ? 'down' : 'neutral'}
          size="large"
        />

        {/* Total Usuarios */}
        <FinancialCard
          title="Total Usuarios"
          value={stats ? formatNumber(stats.totalUsers) : 0}
          icon={<UserOutlined />}
          color="#1890ff"
          loading={loading}
          trend="neutral"
          size="medium"
        />

        {/* Ingresos */}
        <FinancialCard
          title="Ingresos"
          value={stats ? formatCurrency(stats.totalIncome) : '$0'}
          icon={<RiseOutlined />}
          color="#52c41a"
          loading={loading}
          trend="up"
          size="medium"
        />

        {/* Egresos */}
        <FinancialCard
          title="Egresos"
          value={stats ? formatCurrency(stats.totalExpense) : '$0'}
          icon={<FallOutlined />}
          color="#f5222d"
          loading={loading}
          trend="down"
          size="medium"
        />
      </div>

      {/* Estilos de animación */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}
      </style>
    </div>
  );
};